/**
 * Centralised PNR record route resolver.
 *
 * The platform uses human-readable PNR URLs of the form `/pnr/<gds-prefix>-<locator>`
 * (e.g. `/pnr/1s-9v6bkc` for Sabre, `/pnr/1a-4fgtk2` for Amadeus). The legacy
 * `/pnr/record/:id` GUID route is still served, but every internal navigator
 * should call `resolvePnrRoute` so links stay shareable and consistent.
 */

const GDS_PREFIX: Record<string, string> = {
  sabre: '1s',
  amadeus: '1a',
  galileo: '1g',
  worldspan: '1p',
  travelport: '1v',
  apollo: '1u',
};

const PREFIX_TO_GDS: Record<string, string> = Object.fromEntries(
  Object.entries(GDS_PREFIX).map(([gds, prefix]) => [prefix, gds]),
);

const LOCATOR_RX = /^[A-Z0-9]{5,8}$/;
const SLUG_RX = /^([a-z0-9]{2})-([A-Z0-9]+)$/i;

export interface PnrLike {
  id?: string | null;
  readable_id?: number | string | null;
  record_id?: number | string | null;
  record_locator?: string | null;
  recordLocator?: string | null;
  gds_source?: string | null;
  gdsSource?: string | null;
}

/**
 * Build the canonical /pnr/<prefix>-<locator> URL for a stored PNR.
 *
 * Falls back to the numeric /pnr/record/<readable_id> (or legacy /pnr/record/<id>)
 * route when locator/GDS data is missing, so callers never produce broken links.
 */
export function resolvePnrRoute(
  pnr: PnrLike | null | undefined,
  legacyId?: string | number | null,
): string {
  if (!pnr && legacyId != null && String(legacyId).trim() !== '') return `/pnr/record/${legacyId}`;
  if (!pnr) return '/pnr';

  const locator = (pnr.record_locator ?? pnr.recordLocator ?? '').trim();
  const gds = (pnr.gds_source ?? pnr.gdsSource ?? '').trim().toLowerCase();
  const prefix = GDS_PREFIX[gds];

  if (locator && prefix && LOCATOR_RX.test(locator.toUpperCase())) {
    return `/pnr/${prefix}-${locator.toLowerCase()}`;
  }

  const fallbackId = pnr.readable_id ?? pnr.record_id ?? pnr.id ?? legacyId;
  if (fallbackId != null && String(fallbackId).trim() !== '') return `/pnr/record/${fallbackId}`;
  return '/pnr';
}

/**
 * Convenience wrapper for callers that only have a (gds, locator) pair.
 */
export function resolvePnrRouteFromParts(
  gdsSource: string | null | undefined,
  recordLocator: string | null | undefined,
  legacyId?: string | null,
): string {
  return resolvePnrRoute(
    { gds_source: gdsSource ?? null, record_locator: recordLocator ?? null, id: legacyId ?? null },
    legacyId,
  );
}

export interface ParsedPnrSlug {
  prefix: string;
  gdsSource: string | null;
  recordLocator: string;
}

/**
 * Parse a /pnr/:slug param. Returns null when the slug does not match the
 * expected `<prefix>-<locator>` shape. The locator is normalised to UPPERCASE
 * because that is what is stored in `pnr_records.record_locator`.
 */
export function parsePnrSlug(slug: string | null | undefined): ParsedPnrSlug | null {
  if (!slug) return null;
  const m = SLUG_RX.exec(slug);
  if (!m) return null;
  const prefix = m[1].toLowerCase();
  const locator = m[2].toUpperCase();
  if (!LOCATOR_RX.test(locator)) return null;
  return {
    prefix,
    gdsSource: PREFIX_TO_GDS[prefix] ?? null,
    recordLocator: locator,
  };
}

/**
 * Normalise a legacy locator-only URL segment (for example `/pnr/Q7X9P2`).
 *
 * Operational surfaces can receive only a record locator. They use this
 * normalised value for a bounded stored-record lookup before navigating to the
 * canonical `/pnr/<prefix>-<locator>` route.
 */
export function parseLegacyPnrLocator(value: string | null | undefined): string | null {
  const locator = (value ?? '').trim().toUpperCase();
  return LOCATOR_RX.test(locator) ? locator : null;
}

export function isUuid(value: string | null | undefined): boolean {
  if (!value) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}
